import { referenceName } from "../../lib/change-names";
import type { Snapshot } from "../../lib/version";
import type { ReactNode } from "react";
import { LOCALE_CATALOG } from "../../lib/locales";
import { Thumb } from "../ui/Thumb";
import { contentLocaleOf, useI18n, useT, type MsgKey } from "../../lib/i18n";
import { diffBlocks, type Change } from "../../lib/autosave";
import { changedElements, imageValues, isImageUrl, visibleText, isRewardItems, type ItemLike } from "../../lib/change-value";
import { NO_QTY, RANDOM_ITEM_REF, REWARD_LABEL, refLabel, type RewardKind } from "../../lib/reward";
import { DICE_GROUPS, DICE_MORE_GROUP, DICE_SUBGAME_GROUP, FIGHT_GROUPS, FIGHT_MORE_GROUP, FIGHT_TILE_FIELDS, INTIMACY_GROUPS, PETTOWN_GROUPS, PET_STAR_GROUPS } from "../../lib/params";

/**
 * 配置改动怎么写给人看：位置 / 字段 / 值。版本修改记录、完整操作日志、版本对比三处共用，
 * 免得一处改了叫法（比如字段去掉写「不再配置」、种类写「物品卡」不写 item_card），另外两处还露代号。
 */

export const SECTION_KEYS: Record<string, MsgKey> = {
  rivals: "audit.res.rivals", rivalCells: "auditChange.rivalCells", rivalSettings: "audit.res.rivals", pettownRivalPrices: "auditChange.rivalStars", cfg_main_rival: "audit.res.rivals", cfg_main_rival_cell: "auditChange.rivalCells",
  common: "auditChange.global", versionSettings: "auditChange.versionSettings", packSettings: "ver.diff.section.itemPacks",
  levels: "nav.level",
  pets: "nav.pet-list",
  petStars: "nav.pet-star",
  pettown: "nav.pettown-params",
  pettownStars: "nav.pettown-star",
  pettownSlots: "nav.pettown-slot",
  talentNodes: "nav.level-talent",
  boardCells: "nav.board-cells",
  lampDrops: "nav.lamp-drops",
  lamp: "nav.lamp-drops",
  intimacy: "nav.idol-intimacy",
  petStar: "nav.pet-star",
  adoptPools: "ver.diff.section.adoptPools",
  intimacyLevels: "nav.idol-intimacy",
  intimacySources: "nav.idol-intimacy",
  idolPhotos: "nav.idol-photos",
  idolVipPhotos: "nav.idol-vip-photos",
  idolVipVideos: "nav.idol-vip-videos",
  stages: "nav.stage-images",
  stageImages: "nav.stage-images",
  selectors: "nav.init-config",
  dice: "ver.diff.section.dice",
  fight: "ver.diff.section.fight",
  rewards: "ver.diff.section.rewards",
  itemPacks: "ver.diff.section.itemPacks",
  idolPacks: "ver.diff.section.idolPacks",
  auctions: "ver.diff.section.auctions",
  tiles: "ver.diff.section.tiles",
  spins: "ver.diff.section.spins",
  puzzles: "ver.diff.section.puzzles",
  puzzlePrices: "ver.diff.section.puzzlePrices",
  dailyRewards: "ver.diff.section.dailyRewards",
  socialChannels: "ver.diff.section.socialChannels",
  socialEventTypes: "ver.diff.section.socialEventTypes",
  frames: "ver.diff.section.frames",
  matchModes: "ver.diff.section.matchModes",
  battleStages: "ver.diff.section.battleStages",
  abilityCards: "ver.diff.section.abilityCards",
};

const SERVER_FIELDS: Record<string, { field: string; scale: number }> = {
  eTargetMilliB: { field: "E", scale: 1000 },
  costRatioBps: { field: "rho", scale: 10000 },
  unifiedB: { field: "unifiedB", scale: 1 },
  incomeGrowthBps: { field: "incomeK", scale: 10000 },
  targetRollsBase: { field: "targetBase", scale: 1 },
  targetRollsGrowthBps: { field: "targetGrowth", scale: 10000 },
  diceCapBase: { field: "cap", scale: 1 },
  diceCapPerLevel: { field: "capStep", scale: 1 },
  diceCapPerStar: { field: "capStarStep", scale: 1 },
  diceRegenSec: { field: "regen", scale: 60 },
  pDoubleBps: { field: "pDouble", scale: 10000 },
  diceCapMax: { field: "capMax", scale: 1 },
  diceInit: { field: "init", scale: 1 },
  subgameClientPick: { field: "subgamePick", scale: 1 },
  warpProbBps: { field: "warpProb", scale: 100 },
  reliefDiceQty: { field: "reliefQty", scale: 1 },
  reliefDailyTimes: { field: "reliefTimes", scale: 1 },
  reliefDiceThreshold: { field: "reliefThreshold", scale: 1 },
  idleTapPctBps: { field: "idlePct", scale: 10 },
  idleTapMaxBps: { field: "idleMax", scale: 10 },
  idleTapCapPerRoll: { field: "idleCap", scale: 1 },
  dailyCardLimit: { field: "cardLimit", scale: 1 },
  dailyPuzzleLimit: { field: "puzzleLimit", scale: 1 },
  dailyAdLimit: { field: "adLimit", scale: 1 },
  stealPctBps: { field: "stealPct", scale: 10000 },
  stealCapB: { field: "stealCap", scale: 1 },
  shieldMax: { field: "wardCap", scale: 1 },
  luckyKeepDays: { field: "clawbackDays", scale: 1 },
  luckyBonusBps: { field: "clawbackBonus", scale: 10000 },
  repairPctBps: { field: "repairPct", scale: 10000 },
  dailyStolenCap: { field: "visitCap", scale: 1 },
  matchRangeLevels: { field: "matchRange", scale: 1 },
  stealFloorB: { field: "stealFloor", scale: 1 },
  atkSuccessBps: { field: "atkSuccess", scale: 10000 },
  atkBlockB: { field: "atkBlock", scale: 1 },
  thunderPctBps: { field: "thunderPct", scale: 10000 },
  thunderCapB: { field: "thunderCap", scale: 1 },
  thunderFloorB: { field: "thunderFloor", scale: 1 },
  protectNpcLevel: { field: "protectNpc", scale: 1 },
  protectHalfLevel: { field: "protectHalf", scale: 1 },
  dailyGiftCap: { field: "giftCap", scale: 1 },
  stakeTierBase: { field: "stakeTierBase", scale: 1 },
  dailyAskCap: { field: "askCap", scale: 1 },
  friendLimit: { field: "friendLimit", scale: 1 },
  dailySocialEventCap: { field: "socialEventCap", scale: 1 },
  dailyAvatarUploadCap: { field: "avatarUploadCap", scale: 1 },
  dailyUidQueryCap: { field: "uidQueryCap", scale: 1 },
  puzzleHintDailyLimit: { field: "puzzleHintCap", scale: 1 },
  pettownMaxStar: { field: "maxStar", scale: 1 },
  pettownStarsPerLevel: { field: "starsPerLevel", scale: 1 },
  pettownRepairPctBps: { field: "repairPct", scale: 10000 },
  pettownAttackerPctBps: { field: "attackerPct", scale: 10000 },
  pettownProtectStar: { field: "protectStar", scale: 1 },
  pettownMatchRangeStars: { field: "matchRangeStars", scale: 1 },
  petStarMax: { field: "starMax", scale: 1 },
  petMaxedApplyMultiplier: { field: "maxedApplyMultiplier", scale: 1 },
  petMaxedCountsAsAdopt: { field: "maxedCountsAsAdopt", scale: 1 },
  lampPityThreshold: { field: "pityThreshold", scale: 1 },
  intimacyIdolDailyCap: { field: "idolDailyCap", scale: 1 },
  incomeAnchorB: { field: "incomeAnchor", scale: 1 },
  petMaxedCoin: { field: "maxedCoin", scale: 1 },
  bValue: { field: "B", scale: 1 },
  nBuildings: { field: "N", scale: 1 },
  nLevels: { field: "M", scale: 1 },
  rollsRangePct: { field: "rollsRange", scale: 1 },
  starShapeBps: { field: "star", scale: 10000 },
  offsetX: { field: "x", scale: 1 },
  offsetY: { field: "y", scale: 1 },
  isMain: { field: "main", scale: 1 },
  cells: { field: "sys", scale: 1 },
  sortOrder: { field: "sort", scale: 1 },
};

/** 字段名 → 页面上的叫法。参数页的字段直接借参数表单的标签，其余是常见字段 */
const FIELD_KEYS: Record<string, MsgKey> = (() => {
  const out: Record<string, MsgKey> = {
    economyModel: "auditChange.economyModel", moneyScale: "auditChange.moneyScale", num: "auditChange.numerator", den: "auditChange.denominator",
    rivalId: "audit.res.rivals", buildingIdx: "vlog.f.buildings", starLevel: "level.col.M", buildingSet: "initcfg.buildingSet", level: "cost.col.level",
    code: "card.col.code", count: "card.col.count", cards: "pp.group.card", rarity: "dlg.rarity", image: "vlog.f.img", drops: "card.f.drops", starDrops: "card.f.drops", setReward: "card.col.setReward", setRewards: "card.col.setReward", unlockPack: "card.col.unlock", wildcardOk: "card.f.wildcardOk", shopDrawCount: "card.f.shopDrawCount", shopPriceGem: "card.f.shopPriceGem", idolId: "card.f.idol",
    qty: "auditChange.quantity", priceFrag: "pz.price.frag", x: "auditChange.x", y: "auditChange.y", offsetX: "auditChange.x", offsetY: "auditChange.y", main: "auditChange.main", isMain: "auditChange.main", slot: "auditChange.slot", imageType: "auditChange.imageType", gridN: "auditChange.gridN", from: "auditChange.source", thumbImg: "vlog.f.img", cost: "auditChange.cost", name_i18n: "vlog.f.i18n", stageI18n: "vlog.f.stage",
    name: "vlog.f.name", stage: "vlog.f.stage", i18n: "vlog.f.i18n", B: "vlog.f.B",
    targetRolls: "level.col.rolls", targetRollsOverride: "ver.f.targetRollsOverride", costMatrixSeed: "building.matrixSeed", rollsRange: "level.col.range", actualC: "vlog.f.actualC", gMilli: "econ.g.col",
    coverImg: "level.col.coverImg", bgImg: "level.col.bgImg", N: "level.col.N", M: "level.col.M",
    buildings: "vlog.f.buildings", bldInit: "vlog.f.bldInit", star: "vlog.f.star", sys: "vlog.f.sys",
    assets: "vlog.f.assets", levelAssets: "vlog.f.levelAssets", weight: "vlog.f.weight", enabled: "vlog.f.enabled",
    sort: "vlog.f.sort", icon: "vlog.f.icon", img: "vlog.f.img", items: "vlog.f.items", rewards: "vlog.f.rewards",
    line: "talentx.col.line", kind: "vlog.f.kind", value: "vlog.f.value",
    cfg_main_city: "nav.level", cfg_main_city_skin: "vlog.f.assets", cfg_main_building: "vlog.f.buildings",
    cfg_main_cell: "vlog.f.sys", cfg_main_cell_asset: "vlog.f.assets", cfg_talent_node: "nav.level-talent",
    unlockLevel: "card.col.unlock", levelId: "cost.col.level", nameI18n: "vlog.f.i18n", textI18n: "talentx.col.text",
  };
  const groups = [...DICE_GROUPS, DICE_MORE_GROUP, DICE_SUBGAME_GROUP, ...FIGHT_GROUPS, FIGHT_MORE_GROUP, ...INTIMACY_GROUPS, ...PETTOWN_GROUPS, ...PET_STAR_GROUPS];
  for (const g of groups) for (const f of g.fields) out[f.key] ??= f.label;
  for (const fs of Object.values(FIGHT_TILE_FIELDS)) for (const f of fs) out[f.key] ??= f.label;
  return out;
})();

/** `[id=3].buildings[1].actualC` → 行段 `[id=3]` + 字段段 `buildings[1].actualC` */
function splitPath(path: string): { rowId: string | null; rest: string } {
  const m = /^\[([^\]]+)\](.*)$/.exec(path);
  return m ? { rowId: m[1], rest: m[2].replace(/^\./, "") } : { rowId: null, rest: path };
}

export function useChangeLabels(snapshot?: Snapshot, historical = false) {
  const t = useT();
  const { lang } = useI18n();
  const label = (k: MsgKey) => t(k);
  /** 块名 → 页面上的叫法；没登记的原样 */
  const section = (block: string) => (SECTION_KEYS[block] ? label(SECTION_KEYS[block]) : block);
  const where = (c: Change) => {
    const sec = section(c.block);
    const { rowId } = splitPath(c.path);
    if (!rowId) return sec;
    const parts = rowId.split(",").map((part) => { try { return decodeURIComponent(part); } catch { return part; } });
    const primary = c.block === "levels" ? parts.find((part) => part.startsWith("level=")) ?? parts[0] : parts[0];
    const id = primary.replace(/^[a-zA-Z]+=/, "");
    const extra = parts.filter((part) => part !== primary).map((part) => {
      const at = part.indexOf("="), key = part.slice(0, at), value = part.slice(at + 1);
      return at < 0 ? part : `${FIELD_KEYS[key] ? label(FIELD_KEYS[key]) : key} ${value}`;
    });
    const building = c.block === "levels" && /\.cfg_main_building\./.test(c.path);
    const named = c.row ? building ? ` · ${t("vlog.f.buildings")}「${c.row}」` : `「${c.row}」` : "";
    const row = (c.block === "levels" ? t("ver.levelShort", { id }) + named : c.row ? `${id}「${c.row}」` : id) + (extra.length ? ` · ${extra.join(" · ")}` : "");
    return `${sec} · ${row}`;
  };
  const field = (c: Change) => {
    const { rest } = splitPath(c.path);
    if (!rest) return c.op === "add" ? t("vlog.addRow") : c.op === "remove" ? t("vlog.removeRow") : t("vlog.wholeRow");
    return rest.split(".").filter((seg) => !seg.startsWith("cfg_") || ["cfg_main_building", "cfg_main_cell", "cfg_main_cell_asset", "cfg_main_city_skin"].includes(seg)).map((seg) => {
      const m = /^([^[]*)((?:\[[^\]]+\])*)$/.exec(seg);
      const rawKey = m?.[1] ?? seg;
      const key = SERVER_FIELDS[rawKey]?.field ?? rawKey;
      if ((rawKey === "cells" || rawKey === "sys") && /^\[\d+\]$/.test(m?.[2] ?? "")) return t("auditChange.buildingCosts", { n: Number(m![2].slice(1, -1)) + 1 });
      const idx = [...(m?.[2] ?? "").matchAll(/\[([^\]]+)\]/g)].map((x) => (/^\d+$/.test(x[1]) ? `#${Number(x[1]) + 1}` : x[1].replace(/^[a-zA-Z]+=/, "")));
      const name = key ? (FIELD_KEYS[key] ? label(FIELD_KEYS[key]) : LOCALE_CATALOG.find((l) => l.id === key)?.label ?? key) : "";
      return [name, ...idx].filter(Boolean).join(" ");
    }).join(" · ");
  };
  /**
   * 改动一头的值。字段整个没了（换种类后新种类不配它，比如物品卡没有数量）写「不再配置」/「未配置」，
   * 不写「空」——「空」读起来像填成了空值；奖励种类按页面上的叫法显示，不露 item_card 这种代号
   */
  const side = (c: Change, end: "from" | "to"): unknown => {
    const raw = c[end];
    const last = c.path.split(".").at(-1) ?? "";
    const scale = SERVER_FIELDS[last]?.scale ?? 1;
    const scaled = (v: unknown) => scale !== 1 && (typeof v === "number" || typeof v === "string") && Number.isFinite(Number(v)) ? Number(v) / scale : v;
    const v = Array.isArray(raw) && scale !== 1 ? raw.map(scaled) : scaled(raw);
    // 审计记录是 JSON，字段没了就是键不在；版本对比直接比快照，键在、值是 undefined。两种都算「没这个字段」
    if (!c.op && v === undefined) return end === "to" ? UNSET_TO : UNSET_FROM;
    if (typeof v === "string" && KIND_BLOCKS.has(c.block) && /(?:^|\.)kind$/.test(splitPath(c.path).rest)) {
      const k = `spin.kind.${v}` as MsgKey, s = t(k);
      if (s !== k) return s;
    }
    return v;
  };
  const value = (v: unknown, _row?: string): string => {
    if (v === UNSET_TO) return t("vlog.unset");
    if (v === UNSET_FROM) return t("vlog.notSet");
    if (v === undefined || v === null || v === "") return t("vlog.empty");
    if (Array.isArray(v)) return v.every((x) => x == null || typeof x !== "object") ? v.map((x) => (x == null ? "—" : String(x))).join(", ") : JSON.stringify(v);
    if (typeof v === "object") return JSON.stringify(v);
    if (typeof v === "string" && ["stake_tier", "stamina_cap", "shield_slot", "sr_rate", "friend_slots", "match_bonus", "raid_yield", "daily_stamina", "dice_regen", "gift_cap", "gift", "tree_growth", "tree_cap", "tree_count", "puzzle_hint", "auto_roll"].includes(v)) return t(`talentx.line.${v}` as MsgKey);
    if (typeof v === "boolean") return v ? t("vlog.yes") : t("vlog.no");
    return typeof v === "string" ? visibleText(v) : String(v);
  };
  /** 奖励项一行：「物品卡 · 今日穿搭 · 墨镜 × 2」。失效的卡牌引用照 refLabel 标出来——改前改后显示名可能一样，不标看不出改了什么 */
  const itemLine = (x: ItemLike) => {
    const kind = ({ item_card: "itemCard", idol_card: "idolCard", act_item: "actItem", coins: "money" }[x.kind] ?? x.kind) as RewardKind;
    const giftKey = `talentx.gift.kind.${x.kind}` as MsgKey;
    const name = REWARD_LABEL[kind] ? t(REWARD_LABEL[kind]) : t(giftKey) !== giftKey ? t(giftKey) : x.kind;
    const rawRef = typeof x.ref === "string" ? x.ref : "";
    const own = referenceName(snapshot, kind, rawRef, contentLocaleOf(lang));
    const label = own || (historical && ["itemCard", "idolCard", "frame"].includes(kind) && rawRef !== RANDOM_ITEM_REF ? rawRef : refLabel(kind, rawRef, t));
    const ref = rawRef ? ` · ${label}` : "";
    const qty = NO_QTY.includes(kind) ? "" : ` × ${x.qty == null || x.qty === "" ? t("common.pending") : String(x.qty)}`;
    return name + ref + qty + (["spinCap", "moneyStar"].includes(x.kind) ? "%" : "");
  };
  /**
   * 表格里的值：图片地址画成能点开的缩略图，长串截短（悬停看全），对象 / 数组写成几行人话，其余同 value。
   * `other` 是另一头的值：数组只列和它不一样的那几项
   */
  const cellValue = (v: unknown, row?: string, other?: unknown): ReactNode => {
    if (typeof v === "string" && isImageUrl(v)) return <Thumb src={v} className="h-10 w-10 rounded-sm border border-line-lighter object-cover" />;
    if (Array.isArray(v) && v.some((x) => (x && typeof x === "object") || (typeof x === "string" && isImageUrl(x)))) {
      const { shown, hidden } = changedElements(v, other);
      const line = (x: unknown, counterpart: unknown) => (isRewardItems([x]) ? itemLine(x as ItemLike) : cellValue(x, undefined, counterpart));
      return (
        <ul className="space-y-0.5">
          {shown.map(({ index, item, counterpart }) => <li key={index}>{line(item, counterpart)}</li>)}
          {!shown.length && <li>{t("vlog.empty")}</li>}
          {/* inline-block：改前那格整格删除线，不让它划到这行说明上 */}
          {hidden > 0 && <li className="inline-block text-xs font-normal text-ink-placeholder">{t("vlog.sameRest", { n: hidden })}</li>}
        </ul>
      );
    }
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const o = other && typeof other === "object" && !Array.isArray(other) ? other as Record<string, unknown> : null;
      // 和另一头比，只列变了的字段
      const media = imageValues(v);
      const mediaKeys = new Set(media.map(([k]) => k));
      const object = v as Record<string, unknown>;
      const context = [object.code, object.name].filter((x): x is string => typeof x === "string" && !!x).join(" · ");
      const keys = [...new Set([...Object.keys(v), ...Object.keys(o ?? {})])];
      const entries = keys.filter((k) => !mediaKeys.has(k) && k !== "id" && k !== "buildingSystem" && (!o || JSON.stringify(object[k]) !== JSON.stringify(o[k])))
        .map((k) => [k, k in object ? object[k] : UNSET_FROM] as const);
      return (
        <div className="space-y-1">
          {media.length > 0 && <div className="inline-block space-y-1">
            {context && <div className="text-xs font-medium">{context}</div>}
            <div className="flex flex-wrap gap-2">{media.map(([key, src]) => <div key={key} className="inline-block">
              <Thumb src={src} alt={context || key} title={context || key} className="h-12 w-12 rounded-sm border border-line-lighter object-cover" />
              {o?.[key] === src && <div className="text-[10px] font-normal text-ink-placeholder">{t("auditChange.imageUnchanged")}</div>}
            </div>)}</div>
          </div>}
          <ul className="space-y-0.5">
          {entries.map(([k, x]) => <li key={k}>{FIELD_KEYS[k] ? label(FIELD_KEYS[k]) : LOCALE_CATALOG.find((l) => l.id === k)?.label ?? k}：{cellValue(x, undefined, o?.[k])}</li>)}
          {!entries.length && !media.length && <li>{t("vlog.empty")}</li>}
          </ul>
        </div>
      );
    }
    const text = value(v, row);
    const rendered = <span className="whitespace-pre-wrap" title={text.includes("␠") || /\\[nrtu]/.test(text) ? t("auditChange.whitespaceHint") : undefined}>{text}</span>;
    return text.length > 60 ? <details><summary className="cursor-pointer">{text.slice(0, 58)}…</summary><span className="whitespace-pre-wrap break-all">{rendered}</span></details> : rendered;
  };
  const summary = (c: Change) => {
    const complex = c.from && typeof c.from === "object" || c.to && typeof c.to === "object";
    const sub = !c.op && complex ? diffBlocks({ value: c.from }, { value: c.to }, ["value"])[0] : null;
    const focus = sub ? { ...c, path: c.path + (sub.path && !sub.path.startsWith("[") ? "." : "") + sub.path, from: sub.from, to: sub.to } : c;
    const name = `${sub?.row ? `${sub.row} · ` : ""}${field(focus)}`;
    const before = side(focus, "from"), after = side(focus, "to");
    return { field: name, from: isImageUrl(before) ? t("auditChange.imageValue") : value(before), to: isImageUrl(after) ? t("auditChange.imageValue") : value(after) };
  };
  return { where, field, side, value, cellValue, section, summary };
}
/** 种类是掉落项种类（spin.kind.*）的块；别的块也有 kind 字段（萌宠小镇 normal / stamina_tree 等），不套这套叫法 */
const KIND_BLOCKS = new Set(["spins", "lampDrops"]);
const UNSET_TO = Symbol("unset-to"), UNSET_FROM = Symbol("unset-from");

